import { z } from 'zod';

/**
 * Canonical role taxonomy.
 *
 * Hierarchy (most → least privileged):
 *   admin           — platform-level, full access to everything.
 *   project_manager — owns a build, invites, manages costs/margins.
 *   foreman         — mestre de obra. Supervises crew, manages stages/logs.
 *   worker          — trabalhador. Logs work, uploads photos.
 *   client          — proprietário. Read-only status + PIX payment.
 *   broker          — corretor. Read-only marketing view. No costs, no PIX.
 *   buyer           — comprador profissional. Acesso ao módulo Compras
 *                     (cross-obra) sem virar PM. Não edita orçamento,
 *                     cronograma ou membros.
 *   controller      — financeiro/controller. Acesso ao módulo Dinheiro
 *                     (cross-obra) sem virar PM. Modela separação de
 *                     função (SoD).
 *
 * Role stacking on a project (see project_members table):
 *   - project_manager may also hold foreman and/or worker on the same project.
 *   - foreman may also hold worker on the same project.
 *   - client, broker, and bare worker are solo — they don't stack.
 * Access checks use the HIGHEST role the viewer holds on the project.
 *
 * W149 · 2026-05-28 · buyer + controller adicionados.
 */
export const userRoleSchema = z.enum([
  'admin',
  'project_manager',
  'foreman',
  'worker',
  'client',
  'broker',
  'buyer',
  'controller',
]);
export type UserRole = z.infer<typeof userRoleSchema>;

export const loginRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(8).max(200),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const registerRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(200)
    .regex(/[A-Za-z]/, 'Password must contain a letter')
    .regex(/[0-9]/, 'Password must contain a number'),
  fullName: z.string().trim().min(1).max(120),
  phone: z
    .string()
    .trim()
    .regex(/^\+?[0-9 ()-]{7,20}$/, 'Invalid phone')
    .optional(),
  role: userRoleSchema.optional(),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const authUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  fullName: z.string(),
  role: userRoleSchema,
  phone: z.string().nullable(),
  avatarUrl: z.string().url().nullable(),
  /**
   * Wave 279: `pixKey` was previously bundled here and shipped on every
   * authenticated request — flagged by the external auditor as a data-
   * minimization failure (financial identifier ending up in React Query
   * caches and session payloads on every refresh). The user's own pix
   * is now fetched via `GET /v1/auth/me/pix-key` only by the
   * SettingsPage profile editor. Team-coordination surfaces (project
   * members, suppliers, owner-project PM card) keep pixKey intact —
   * those are intentional gated views, not always-loaded session data.
   */
  createdAt: z.string(),
  // Wave 379BK · #101 · tenantId no AuthUser. Pode ser opcional pra
  // tokens emitidos antes da migração — fallback DEFAULT_TENANT_ID
  // no consumer. Multi-tenant futuro: required.
  tenantId: z.string().nullable().optional(),
  /**
   * Wave WelcomeFlow · 2026-05-20 — first-run gate.
   * NULL = ainda não passou pela WelcomePage. Quando NULL E o usuário
   * tem 0 projetos, a web renderiza /welcome no lugar do redirect
   * pra /projects ou /campo. Setado via POST /v1/users/me/welcomed.
   */
  welcomedAt: z.string().nullable().optional(),
  /**
   * W123.13 · 2026-05-26 — Playground preference.
   * 'real' (default) ou 'playground'. Login flow lê isto pra decidir
   * landing pós-login. Toggle via POST /v1/playground/enter|exit.
   * Optional pra tokens emitidos antes da migração — fallback 'real'.
   */
  activeMode: z.enum(['real', 'playground']).optional(),
});
export type AuthUser = z.infer<typeof authUserSchema>;

/** Wave 279 — narrow lazy-load shape for the SettingsPage profile editor. */
export const authPixKeyResponseSchema = z.object({
  pixKey: z.string().nullable(),
});
export type AuthPixKeyResponse = z.infer<typeof authPixKeyResponseSchema>;

export const authSessionSchema = z.object({
  token: z.string(),
  expiresAt: z.string(),
  user: authUserSchema,
});
export type AuthSession = z.infer<typeof authSessionSchema>;

export const updateProfileRequestSchema = z.object({
  fullName: z.string().trim().min(1).max(120).optional(),
  phone: z
    .string()
    .trim()
    .regex(/^\+?[0-9 ()-]{7,20}$/, 'Invalid phone')
    .nullable()
    .optional(),
  avatarUrl: z.string().url().nullable().optional(),
  /**
   * PIX key. We accept an opaque trimmed string (max 140 — real PIX keys are
   * at most 77 chars, but we leave headroom for future formats). Null clears
   * the key so the member card falls back to "no PIX key".
   */
  pixKey: z.string().trim().max(140).nullable().optional(),
});
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(200)
    .regex(/[A-Za-z]/, 'Password must contain a letter')
    .regex(/[0-9]/, 'Password must contain a number'),
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

/**
 * Google Sign-In request. The client receives an ID token (JWT) from Google
 * Identity Services and forwards it to us — we verify it server-side against
 * Google's JWKS and then issue our own session token. The `role` field lets a
 * first-time user choose their role during the Google sign-up flow; it's
 * ignored for users that already exist.
 */
export const googleSignInRequestSchema = z.object({
  idToken: z.string().min(10).max(4096),
  role: userRoleSchema.optional(),
});
export type GoogleSignInRequest = z.infer<typeof googleSignInRequestSchema>;

/**
 * Wave 13.2 — password reset.
 *
 * `forgotPasswordRequestSchema` is intentionally permissive on the email
 * field (trimmed + lowercased + length cap). The server always responds
 * with `{ ok: true }` regardless of whether the email exists, to prevent
 * account enumeration. Actual validity is decided downstream.
 *
 * `resetPasswordRequestSchema` enforces the same strength rules as
 * `changePasswordRequestSchema` so a user who chose a strong password on
 * signup can't soft-land on a weak one via the reset flow.
 */
export const forgotPasswordRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
});
export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequestSchema>;

export const resetPasswordRequestSchema = z.object({
  token: z.string().min(16).max(512),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(200)
    .regex(/[A-Za-z]/, 'Password must contain a letter')
    .regex(/[0-9]/, 'Password must contain a number'),
});
export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>;

/**
 * Wave 13.3 — admin user management.
 *
 * The admin UI creates users directly (without going through the public
 * signup form) and can toggle role/disabled state. A server-generated
 * temporary password is returned from the create endpoint once — the
 * admin shares it out-of-band, and the user is pushed to reset on first
 * login.
 */
export const adminCreateUserRequestSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  fullName: z.string().trim().min(1).max(120),
  role: userRoleSchema,
  phone: z
    .string()
    .trim()
    .regex(/^\+?[0-9\s()-]{6,32}$/, 'Phone must be a valid number')
    .optional(),
});
export type AdminCreateUserRequest = z.infer<typeof adminCreateUserRequestSchema>;

export const adminUpdateUserRequestSchema = z.object({
  fullName: z.string().trim().min(1).max(120).optional(),
  role: userRoleSchema.optional(),
  phone: z.string().trim().max(32).nullable().optional(),
  disabled: z.boolean().optional(),
});
export type AdminUpdateUserRequest = z.infer<typeof adminUpdateUserRequestSchema>;

export const adminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  fullName: z.string(),
  role: userRoleSchema,
  phone: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  createdAt: z.string(),
  disabledAt: z.string().nullable(),
  lastLoginAt: z.string().nullable(),
  /** Wave 33 — approval gate. NULL while pending. */
  approvedAt: z.string().nullable(),
  approvedByUserId: z.string().nullable(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

// Wave 33 — approval queue.
export const approveUserAssignmentSchema = z.object({
  projectId: z.string(),
  role: userRoleSchema,
});
export type ApproveUserAssignment = z.infer<typeof approveUserAssignmentSchema>;

export const approveUserRequestSchema = z.object({
  /**
   * At least one project the new user will be a member of. The admin
   * UI enforces "pick at least one" so the server can assume length > 0
   * here, but we enforce it with Zod too.
   */
  assignments: z.array(approveUserAssignmentSchema).min(1),
});
export type ApproveUserRequest = z.infer<typeof approveUserRequestSchema>;
